import { DateTime } from 'luxon';
import { CoachClientSlot, CoachClientStatus, CoachPresenceDoc, PRESENCE_ONLINE_THRESHOLD_MS } from '@cf-shared/data-model/coach';
import { describeError } from '@cf-shared/error-describe';

/**
 * Presence state exposed by `CoachPresenceService` to UI consumers.
 */
export interface PresenceState {
  online: boolean;
  lastSeenUtc?: string;
}

/**
 * Asks the caller whether the client a coach's remote read was refused for is still
 * on the coach's list. Supplied by the coach client list, whose local list can be out
 * of date on a warm resume (df-ad81a785446c). See `judgeAuthDenialAsync`.
 */
export type StillClaimedCheck = () => Promise<boolean>;

/** Why `judgeAuthDenialAsync` decided a 401/403 is expected and not worth reporting. */
export enum AuthDenialSkipReason {
  /** A staff account is impersonating the coach; it is never a member of the client's database (#6663). */
  Impersonating = 'Impersonating',
  /** The coach has removed this client, so the server is right to refuse (df-ad81a785446c). */
  NoLongerClaimed = 'NoLongerClaimed',
}

/** What `judgeAuthDenialAsync` decided about one 401/403. */
export interface AuthDenialVerdict {
  /** True when the denial must be reported as a problem. */
  report: boolean;
  /** Set when `report` is false: why the denial is expected. */
  skipReason?: AuthDenialSkipReason;
  /** Set when the still-claimed check itself failed; the denial is then reported with it. */
  checkError?: string;
}

/**
 * Decide whether a 401/403 from a coach's read of a client's database is worth
 * reporting. Never while a staff account impersonates the coach: the admin signs in
 * to CouchDB as itself and is never a member of the coach's client databases, so
 * every such read is refused (#6663, `design-docs/coach-mode.md`, "Admin/support
 * impersonation"). Otherwise, without a check, always (the old rule). With one, only
 * while the client is still claimed: a denial for a client the coach has removed is
 * what the server should answer, not a lockout. A check that fails counts as still
 * claimed, so a real lockout is never hidden by a broken check.
 */
export async function judgeAuthDenialAsync(
  impersonating: boolean,
  stillClaimedAsync?: StillClaimedCheck,
): Promise<AuthDenialVerdict> {
  // if staff are impersonating, every read is refused; skip before any sync wait starts
  if (impersonating) return { report: false, skipReason: AuthDenialSkipReason.Impersonating };
  if (!stillClaimedAsync) return { report: true };
  try {
    const claimed = await stillClaimedAsync();
    return claimed ? { report: true } : { report: false, skipReason: AuthDenialSkipReason.NoLongerClaimed };
  } catch (err) {
    return { report: true, checkError: describeError(err) };
  }
}

/** The `console.log` text for a 401/403 that `judgeAuthDenialAsync` decided not to report. */
export function describeSkippedAuthDenial(skipReason: AuthDenialSkipReason | undefined): string {
  return skipReason === AuthDenialSkipReason.Impersonating
    ? 'a staff account is impersonating the coach and is never a member of the client\'s database, so this is expected'
    : 'the client is no longer claimed, so this is expected';
}

/**
 * Whether `clientUid` still holds a claimed slot in `slots`, for `judgeAuthDenialAsync`.
 * A sleeping slot counts: sleep hides a client from the coach's list but does not end
 * the coach's access to their database (`design-docs/coach-mode.md`), so a denial for
 * a sleeping client is a real lockout and must still be reported.
 */
export function isClientStillClaimed(slots: CoachClientSlot[] | null | undefined, clientUid: string): boolean {
  return (slots || []).some(slot => slot?.status === CoachClientStatus.Claimed && slot.clientUid === clientUid);
}

/**
 * Pure helper: compact relative-time label for "Last seen X ago". Optional
 * `nowUtc` allows tests to pin the reference clock.
 */
export function formatTimeSince(lastSeenUtc: string, nowUtc?: DateTime): string {
  const now = nowUtc ?? DateTime.utc();
  const diffMs = Math.max(0, now.diff(DateTime.fromISO(lastSeenUtc)).as('milliseconds'));
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return '1m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/**
 * Pure helper: derive the online/offline `PresenceState` from a (possibly
 * missing) presence doc. Optional `nowUtc` allows tests to pin the reference
 * clock.
 */
export function computeOnlineState(
  doc: CoachPresenceDoc | null | undefined,
  nowUtc?: DateTime,
): PresenceState {
  if (!doc?.lastSeenUtc) return { online: false };
  const now = nowUtc ?? DateTime.utc();
  const ageMs = now.diff(DateTime.fromISO(doc.lastSeenUtc)).as('milliseconds');
  const online = ageMs < PRESENCE_ONLINE_THRESHOLD_MS;
  return { online, lastSeenUtc: doc.lastSeenUtc };
}
